import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, Newspaper, Eye, ExternalLink, RotateCcw } from 'lucide-react'
import { NEWS_CATEGORIES } from '@/modules/news/config/news.config'
import { useAdminArticlesStore } from '../../../state/articlesStore'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { useToast } from '../../components/Toast'

export function ArticlesListPage() {
  const articles = useAdminArticlesStore((s) => s.articles)
  const remove = useAdminArticlesStore((s) => s.remove)
  const reset = useAdminArticlesStore((s) => s.reset)
  const fetchAll = useAdminArticlesStore((s) => s.fetchAll)
  const loading  = useAdminArticlesStore((s) => s.loading)
  useEffect(() => { fetchAll() }, [fetchAll])
  const push = useToast((s) => s.push)

  const [q, setQ] = useState('')
  const [cat, setCat] = useState('')
  const [status, setStatus] = useState<'' | 'draft' | 'published'>('')
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [resetConfirm, setResetConfirm] = useState(false)

  const filtered = useMemo(() => {
    let items = articles.slice()
    if (q.trim()) {
      const needle = q.trim().toLowerCase()
      items = items.filter((a) =>
        a.title.toLowerCase().includes(needle) ||
        a.slug.toLowerCase().includes(needle) ||
        a.author.toLowerCase().includes(needle),
      )
    }
    if (cat) items = items.filter((a) => a.category === cat)
    if (status) items = items.filter((a) => a.status === status)
    return items
  }, [articles, q, cat, status])

  const target = deleteId ? articles.find((a) => a.id === deleteId) : null

  const handleDelete = async () => {
    if (!deleteId || !target) return
    try { await remove(deleteId); push('success', `Đã xóa "${target.title}"`) }
    catch (err: any) { push('error', err?.message ?? 'Xóa thất bại') }
    setDeleteId(null)
  }

  const draftCount = articles.filter((a) => a.status === 'draft').length
  const pubCount = articles.length - draftCount

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Tin tức</h1>
          <p className="text-sm text-muted mt-1">
            {articles.length} bài viết — <span className="text-green-600 font-semibold">{pubCount} xuất bản</span> · <span className="text-amber-600 font-semibold">{draftCount} nháp</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setResetConfirm(true)}
            className="h-11 px-4 rounded-md border border-border text-text hover:bg-slate-50 font-medium text-sm inline-flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" /> Khôi phục
          </button>
          <Link
            to="/tin-tuc"
            target="_blank"
            className="h-11 px-4 rounded-md border border-border text-text hover:bg-slate-50 font-medium text-sm inline-flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" /> Xem trang khách
          </Link>
          <Link
            to="/admin/tin-tuc/moi"
            className="h-11 px-5 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Viết bài mới
          </Link>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-xl shadow-card p-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tìm tiêu đề, slug, tác giả..."
            className="w-full h-10 rounded-md border border-border bg-surface pl-9 pr-3 text-sm outline-none focus:border-brand-500"
          />
        </div>
        <select
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="h-10 rounded-md border border-border bg-surface px-3 text-sm outline-none focus:border-brand-500 min-w-[150px]"
        >
          <option value="">Tất cả chuyên mục</option>
          {NEWS_CATEGORIES.filter((c) => c !== 'Tất cả').map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as any)}
          className="h-10 rounded-md border border-border bg-surface px-3 text-sm outline-none focus:border-brand-500 min-w-[140px]"
        >
          <option value="">Mọi trạng thái</option>
          <option value="published">Đã xuất bản</option>
          <option value="draft">Nháp</option>
        </select>
        <div className="text-sm text-muted ml-auto">Hiển thị <span className="font-semibold text-text">{filtered.length}</span> / {articles.length}</div>
      </div>

      <div className="bg-surface border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-muted text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Bài viết</th>
                <th className="text-left px-4 py-3 font-medium">Chuyên mục</th>
                <th className="text-left px-4 py-3 font-medium">Tác giả</th>
                <th className="text-center px-4 py-3 font-medium">Đọc</th>
                <th className="text-center px-4 py-3 font-medium">Trạng thái</th>
                <th className="text-left px-4 py-3 font-medium">Ngày</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-muted mb-3">
                      <Newspaper className="w-8 h-8" />
                    </div>
                    <div className="font-semibold text-text">Không tìm thấy bài viết</div>
                    <div className="text-sm text-muted mt-1">Thử đổi từ khóa hoặc chuyên mục khác</div>
                  </td>
                </tr>
              )}
              {filtered.map((a) => (
                <tr key={a.id} className="border-t border-border hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-12 rounded-md bg-slate-100 overflow-hidden border border-border shrink-0">
                        <img
                          src={a.image}
                          alt={a.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 9"><rect width="16" height="9" fill="%23e2e8f0"/></svg>'
                          }}
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-text line-clamp-2 leading-snug max-w-[420px]">{a.title}</div>
                        <div className="text-xs text-muted font-mono mt-0.5">{a.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block bg-brand-50 text-brand-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">{a.category}</span>
                  </td>
                  <td className="px-4 py-3 text-muted">{a.author}</td>
                  <td className="px-4 py-3 text-center text-xs text-muted">{a.readTime} phút</td>
                  <td className="px-4 py-3 text-center">
                    {a.status === 'published'
                      ? <span className="bg-green-50 text-green-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">Đã xuất bản</span>
                      : <span className="bg-amber-50 text-amber-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">Nháp</span>}
                  </td>
                  <td className="px-4 py-3 text-muted text-xs whitespace-nowrap">{a.date}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {a.status === 'published' && (
                        <Link
                          to={`/tin-tuc/${a.slug}`}
                          target="_blank"
                          className="w-8 h-8 rounded-md hover:bg-slate-100 text-muted flex items-center justify-center transition"
                          aria-label="Xem"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                      )}
                      <Link
                        to={`/admin/tin-tuc/${a.id}`}
                        className="w-8 h-8 rounded-md hover:bg-brand-50 text-brand-600 flex items-center justify-center transition"
                        aria-label="Sửa"
                      >
                        <Edit className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => setDeleteId(a.id)}
                        className="w-8 h-8 rounded-md hover:bg-red-50 text-danger flex items-center justify-center transition"
                        aria-label="Xóa"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteId}
        title="Xóa bài viết"
        message={`Bạn chắc chắn muốn xóa "${target?.title}"? Hành động không thể hoàn tác.`}
        confirmLabel="Xóa"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      <ConfirmDialog
        open={resetConfirm}
        title="Khôi phục mặc định"
        message="Thao tác này sẽ nạp lại danh sách bài viết mặc định và xóa mọi thay đổi hiện tại. Tiếp tục?"
        confirmLabel="Khôi phục"
        tone="brand"
        onConfirm={async () => { await reset(); push('info', 'Đã refresh từ server'); setResetConfirm(false) }}
        onCancel={() => setResetConfirm(false)}
      />
    </div>
  )
}
